import {
  type FunctionComponent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from '@ardoq/rxbeach';
import { FlexBox } from '@ardoq/layout';
import { t } from '@ardoq/i18n';
import { ViewIds } from '@ardoq/api-types';
import { HeightOffset } from 'views/viewLegend/useViewLegendSubscription';
import { ViewLegendContainer } from '@ardoq/graph';
import { ViewLegend } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import {
  BlocksVisualState,
  type AqBlocks,
  type BlocksActivatedEvent,
  type BlocksHoveredEvent,
  type BlocksSelectedEvent,
} from 'tabview/visualizations/aq-blocks/types';
import { createItemSet } from 'tabview/visualizations/aq-blocks/itemSet';
import type { AqFindElement } from 'tabview/visualizations/aq-find/aq-find';
import type { AqQueryChangedEvent } from 'tabview/visualizations/aq-find/events';
import {
  subscribeToStoreSelectionCleared,
  toGraphSelection,
} from './selection';
import { collectOneHopTrace } from './collectOneHopTrace';
import { findNode } from './findNode';
import { CenteredMessage } from 'tabview/CenteredMessage';
import { useRepaintOnImageReady } from 'tabview/assistantShared/useRepaintOnImageReady';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
// Registers the <aq-blocks> custom element (customElements.define side effect).
import 'tabview/visualizations/aq-blocks/aq-blocks';
// Registers the <aq-find> custom element (customElements.define side effect).
import 'tabview/visualizations/aq-find/aq-find';
import {
  flexibleBlockDiagramCollapseAll,
  flexibleBlockDiagramExpandAll,
  flexibleBlockDiagramSetCollapsedGroupIds,
  FLEXIBLE_BLOCK_DIAGRAM_ZOOM_ACTIONS,
} from './actions';
import {
  collectGroupNodes,
  type FlexibleBlockDiagramViewModel,
  getViewModel$,
} from './viewModel$';
import { activatedComponentId } from './doubleClick';
import { useBlocksTooltip } from 'tabview/assistantShared/blocksTooltip';
import { createContextMenuHandler } from './contextMenu';
import { useZoomActions } from 'tabview/assistantShared/useZoomActions';
import { useSaveGroupCollapse } from 'tabview/assistantShared/collapsedGroups';
import { WILDCARD } from '@ardoq/common-helpers';

export const FlexibleBlockDiagram = (props: FlexibleBlockDiagramViewModel) => {
  const {
    graph,
    componentIds,
    layoutStyle,
    i18nLocale,
    isLegendActive,
    legendProps,
    elementRef,
    containerRef,
    viewSettings,
    isReadOnly,
    commands,
  } = props;
  const ref = useRef<AqBlocks | null>(null);
  const componentIdsRef = useRef(componentIds);
  componentIdsRef.current = componentIds;
  const { doTooltip, tooltipPopover } = useBlocksTooltip();
  const findRef = useRef<AqFindElement | null>(null);
  const [isFindVisible, setIsFindVisible] = useState(false);

  useRepaintOnImageReady(ref);

  const setElement = useCallback(
    (element: AqBlocks | null) => {
      ref.current = element;
      elementRef.current = element;
    },
    [elementRef]
  );

  // The layoutStyle prop drives the aq-blocks relayout on its own; it just does not
  // fit the viewport afterwards, so fit explicitly when the layout type changes.
  useEffect(() => {
    ref.current?.zoomFit();
  }, [layoutStyle.type]);

  const handleHovered = ({ detail: { items } }: BlocksHoveredEvent): void => {
    const element = ref.current;

    if (element && element.isReadonly && !isFindVisible) {
      element.highlighted = collectOneHopTrace(
        element.graph,
        items.nodes(),
        items.links()
      );
      element.pendRender();
    }
  };

  // The field can only take focus once it is shown, so opening it focuses it after the render.
  useEffect(() => {
    const element = ref.current;
    const findField = findRef.current;

    if (isFindVisible && findField) {
      findField.select();

      if (element?.graph) {
        element.highlighted = findNode(element.graph, findField.value);
        element.pendRender();
      }
    }
  }, [isFindVisible]);

  const handleFindRequested = (e: Event): void => {
    e.preventDefault();

    if (isFindVisible) {
      findRef.current?.select();
    } else {
      setIsFindVisible(true);
    }
  };

  const handleFindChanged = ({ detail }: AqQueryChangedEvent): void => {
    const element = ref.current;

    if (element?.graph) {
      element.highlighted = findNode(element.graph, detail.query);
      element.pendRender();
    }
  };

  const handleFindCloseRequested = (): void => {
    const element = ref.current;

    setIsFindVisible(false);

    if (element) {
      element.highlighted = createItemSet(BlocksVisualState.None);
      element.pendRender();
      element.focus();
    }
  };

  const handleFindKeyDown = (event: KeyboardEvent<AqFindElement>): void => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      findRef.current?.select();
    }
  };

  const handleSelected = ({ detail: { items } }: BlocksSelectedEvent): void => {
    commands.onSelectionChange(toGraphSelection(items, componentIds));
  };

  // Double-click navigates to the clicked component, matching Block Diagram.
  const handleActivated = ({
    detail: { items },
  }: BlocksActivatedEvent): void => {
    const modelId = activatedComponentId(items);
    if (modelId) commands.onComponentDoubleClick(modelId);
  };

  useZoomActions(ref, FLEXIBLE_BLOCK_DIAGRAM_ZOOM_ACTIONS);
  const { onEdited: handleEdited, applySavedCollapse } = useSaveGroupCollapse(
    commands.onCollapsedGroupsChange,
    viewSettings.collapsedGroupIds
  );

  // Drive the imperative aq-blocks element from the collapse and expand actions, and
  // the two that empty the store's graph selection.
  useEffect(() => {
    const subscriptions = [
      subscribeToAction(flexibleBlockDiagramExpandAll, () =>
        applySavedCollapse([], () => ref.current?.expandAll())
      ),
      subscribeToAction(flexibleBlockDiagramCollapseAll, () =>
        applySavedCollapse([WILDCARD], () => ref.current?.collapseAll())
      ),
      subscribeToAction(
        flexibleBlockDiagramSetCollapsedGroupIds,
        ({ collapsedGroupIds }) =>
          applySavedCollapse(collapsedGroupIds, () => {
            const element = ref.current;
            const graph = element?.graph;
            if (!graph) return;

            const groupNodes = collectGroupNodes(graph);
            const toCollapse = groupNodes.filter(
              node => node.open && collapsedGroupIds.includes(node.id)
            );
            const toExpand = groupNodes.filter(
              node => !node.open && !collapsedGroupIds.includes(node.id)
            );

            element.collapse(toCollapse);
            element.expand(toExpand);
          })
      ),
      // The aq-blocks selection setter only notifies on change, so a canvas
      // selection left standing after the store empties makes re-clicking the
      // same component a no-op.
      ...subscribeToStoreSelectionCleared(
        () => ref.current,
        () => componentIdsRef.current
      ),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, [applySavedCollapse]);

  // Stable identity: aq-blocks cancels the open context menu whenever the
  // doContextMenu prop changes, so recreating it each render would dismiss it.
  const doContextMenu = useMemo(
    () =>
      createContextMenuHandler(
        { viewId: ViewIds.FLEXIBLE_BLOCK_DIAGRAM, i18nLocale },
        commands.closeContextMenu
      ),
    [i18nLocale, commands.closeContextMenu]
  );

  if (!graph) {
    return (
      <CenteredMessage
        title={t(
          i18nLocale,
          'flexibleBlockDiagram:emptyState.title',
          'Nothing to lay out yet'
        )}
        description={t(
          i18nLocale,
          'flexibleBlockDiagram:emptyState.description',
          'This view has no components to lay out.'
        )}
      />
    );
  }

  return (
    // A raw div rather than `Box`: the export pipeline needs a ref to this
    // container to find the legend, and `CommonBoxProps` whitelists the HTML
    // attributes it forwards, `ref` not among them.
    <div
      ref={containerRef}
      className="prevent-context-change"
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <FlexBox flex={1} width="full" height="full" position="relative">
        <aq-blocks
          ref={setElement}
          graph={graph}
          layoutStyle={layoutStyle}
          doTooltip={doTooltip}
          doContextMenu={doContextMenu}
          onHovered={handleHovered}
          onSelected={handleSelected}
          onActivated={handleActivated}
          onEdited={handleEdited}
          onFindRequested={handleFindRequested}
          isReadonly={isReadOnly}
          style={{ position: 'absolute', inset: 0 }}
        />
        <aq-find
          ref={findRef}
          hidden={!isFindVisible}
          find-label={t(i18nLocale, 'flexibleBlockDiagram:find.label', 'Find')}
          close-label={t(
            i18nLocale,
            'flexibleBlockDiagram:find.close',
            'Close'
          )}
          onQueryChanged={handleFindChanged}
          onCloseRequested={handleFindCloseRequested}
          onKeyDown={handleFindKeyDown}
          style={{ position: 'absolute', top: 8, right: 8 }}
        />
      </FlexBox>
      <HeightOffset>
        {heightOffset => (
          <ViewLegendContainer
            heightOffset={heightOffset}
            visible={isLegendActive}
          >
            <ViewLegend
              {...legendProps}
              labels={getViewLegendLabels(i18nLocale)}
            />
          </ViewLegendContainer>
        )}
      </HeightOffset>
      {tooltipPopover}
    </div>
  );
};

export const FlexibleBlockDiagramView: FunctionComponent = connect(
  FlexibleBlockDiagram,
  getViewModel$()
);
